import React, { Fragment, useState } from 'react';
import { Box, Button, Divider, Grid, SelectChangeEvent, Tooltip, Typography } from '@mui/material';
import {
  addElementIdToRecipient,
  removeElementIdFromRecipients,
  selectActiveSidebarById,
  selectRecipients,
  selectedElement,
  updateContentElementById,
} from 'features/document/documentSlice';
import { isQuoteAcceptanceInfoTableElement } from 'utils/typeGuards';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { AzulCheckbox } from '@the-ksquare-group/azul-ds';
import AlignmentRadioGroup from 'components/AlignmentRadioGroup/AlignmentRadioGroup';
import RecipientSelect from 'components/RecipientSelect/RecipientSelect';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import ModeEditRoundedIcon from '@mui/icons-material/ModeEditRounded';
import { Tag } from 'components/RadioTag/RadioTag';
import { parseJustifyContentToAlignment } from 'utils/alignmentParsers';
import { openInNewTab } from 'utils/openInNewTab';
import { selectQuote } from 'features/salesforce/salesforceSlice';

interface QuoteAcceptanceInfoTableSidebarProps {
  currentElement: selectedElement;
}

export default function QuoteAcceptanceInfoTableSidebar({ currentElement }: QuoteAcceptanceInfoTableSidebarProps) {
  if (!isQuoteAcceptanceInfoTableElement(currentElement.data)) {
    return null;
  }

  const dispatch = useAppDispatch();
  const {
    pageId,
    rowId,
    columnId,
    data: { content, recipient },
  } = currentElement;
  const { acceptanceInfo } = content;
  const { signature, date } = acceptanceInfo;

  const activeSidebar = useAppSelector(state =>
    selectActiveSidebarById(state, pageId, rowId, columnId, currentElement.data.id)
  );
  const recipients = useAppSelector(selectRecipients);
  const salesforceBaseUrl = useAppSelector(state => state.auth.salesforceBaseUrl);
  const quote = useAppSelector(selectQuote);

  const parsedSignatureJustifyContent = parseJustifyContentToAlignment(signature.justifyContent);
  const parsedDateJustifyContent = parseJustifyContentToAlignment(date.justifyContent);
  const [signatureAlignment, setSignatureAlignment] = useState<Tag>({
    label: parsedSignatureJustifyContent,
    value: parsedSignatureJustifyContent,
  });
  const [dateAlignment, setDateAlignment] = useState<Tag>({
    label: parsedDateJustifyContent,
    value: parsedDateJustifyContent,
  });

  const handleOnChangeRequired = (newRequired: boolean, type: 'signature' | 'date') => {
    const newContent = {
      ...content,
      acceptanceInfo: {
        ...content.acceptanceInfo,
        [type]: {
          ...content.acceptanceInfo[type],
          required: newRequired,
        },
      },
      activeSidebar: activeSidebar,
    };

    dispatch(
      updateContentElementById({
        content: newContent,
      })
    );
  };

  const handleOnChangeAlignment = (newAlignment: Tag, type: 'signature' | 'date') => {
    if (type === 'signature') {
      setSignatureAlignment(newAlignment);
    } else {
      setDateAlignment(newAlignment);
    }

    const newContent = {
      ...content,
      acceptanceInfo: {
        ...content.acceptanceInfo,
        [type]: {
          ...content.acceptanceInfo[type],
          justifyContent: parseJustifyContentToAlignment(newAlignment.value),
        },
      },
      activeSidebar: activeSidebar,
    };

    dispatch(
      updateContentElementById({
        content: newContent,
      })
    );
  };

  const handleOnChangeRecipient = (event: SelectChangeEvent) => {
    dispatch(removeElementIdFromRecipients(currentElement.data.id));

    const selectedRecipient = recipients.find(recipient => recipient.fullName === event.target.value);
    if (selectedRecipient) {
      dispatch(addElementIdToRecipient({ recipientId: selectedRecipient.id, elementId: currentElement.data.id }));
      const { fullName: recipientName } = selectedRecipient;

      const newContent = {
        ...content,
        acceptanceInfo: {
          ...content.acceptanceInfo,
          name: recipientName,
        },
        table: {
          ...content.table,
          cells: {
            ...content.table.cells,
            'cell-name': {
              ...content.table.cells['cell-name'],
              content: `${recipientName}`,
            },
            'cell-date': {
              ...content.table.cells['cell-date'],
              content: `${recipientName} Date`,
            },
            'cell-signature': {
              ...content.table.cells['cell-signature'],
              content: `${recipientName} Signature`,
            },
          },
        },
        activeSidebar: activeSidebar,
      };

      dispatch(
        updateContentElementById({
          content: newContent,
          recipient: selectedRecipient,
        })
      );
    }
  };

  const getSidebar = (activeSidebar?: string) => {
    switch (activeSidebar) {
      case 'acceptanceInfo':
        return (
          <Fragment>
            <Typography fontSize={'16px'} fontWeight={500} marginBottom={'32px'}>
              Acceptance Info Block
            </Typography>
            {salesforceBaseUrl && quote && (
              <Button
                variant='outlined'
                sx={{
                  padding: '8px 12px',
                  display: 'flex',
                  gap: '8px',
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: '600',
                }}
                onClick={() => openInNewTab(`${salesforceBaseUrl}/lightning/r/Quote/${quote.Id}/edit?`)}
              >
                <ModeEditRoundedIcon />
                Edit Acceptance Info In CRM
              </Button>
            )}
          </Fragment>
        );
      case 'date':
        return (
          <Fragment>
            <Typography fontSize={'16px'} fontWeight={500} marginBottom={'32px'}>
              Date Block
            </Typography>
            <Box display={'flex'} flexDirection={'column'} gap={'24px'} width='100%'>
              <Box alignItems={'center'} display={'flex'} gap='8px'>
                <Typography fontSize={'14px'} fontWeight={500}>
                  Recipient
                </Typography>
                <Tooltip
                  title='The selected recipient will be automatically set for both date and signature fields '
                  arrow
                  placement='top'
                >
                  <InfoRoundedIcon color='primary' />
                </Tooltip>
              </Box>
              <RecipientSelect
                value={recipient}
                recipientOptions={recipients}
                editRecipients={false}
                onChange={handleOnChangeRecipient}
              />
            </Box>
            <Divider
              color='#C9CACB'
              flexItem
              orientation='horizontal'
              variant='middle'
              sx={{ margin: '24px 0', width: '100%' }}
            />
            <AzulCheckbox
              checked={date.required}
              label='Required'
              onChange={e => handleOnChangeRequired(e.target.checked, 'date')}
            />
            <Typography fontSize={'14px'} fontWeight={500} marginTop={'32px'} marginBottom={'24px'}>
              Alignment
            </Typography>
            <Box maxWidth={'148px'}>
              <AlignmentRadioGroup
                selected={dateAlignment}
                handleChange={tag => handleOnChangeAlignment(tag, 'date')}
              />
            </Box>
          </Fragment>
        );
      case 'signature':
        return (
          <Fragment>
            <Typography fontSize={'16px'} fontWeight={500} marginBottom={'32px'}>
              Signature Block
            </Typography>
            <Box display={'flex'} flexDirection={'column'} gap={'24px'} width='100%'>
              <Box alignItems={'center'} display={'flex'} gap='8px'>
                <Typography fontSize={'14px'} fontWeight={500}>
                  Recipient
                </Typography>
                <Tooltip
                  title='The selected recipient will be automatically set for both date and signature fields '
                  arrow
                  placement='top'
                >
                  <InfoRoundedIcon color='primary' />
                </Tooltip>
              </Box>
              <Divider
                color='#C9CACB'
                flexItem
                orientation='horizontal'
                variant='middle'
                sx={{ margin: '24px 0', width: '100%' }}
              />
              <RecipientSelect
                value={recipient}
                recipientOptions={recipients}
                editRecipients={false}
                onChange={handleOnChangeRecipient}
              />
            </Box>
            <AzulCheckbox
              checked={signature.required}
              label='Required'
              onChange={e => handleOnChangeRequired(e.target.checked, 'signature')}
            />
            <Typography fontSize={'14px'} fontWeight={500} marginTop={'32px'} marginBottom={'24px'}>
              Alignment
            </Typography>
            <Box maxWidth={'148px'}>
              <AlignmentRadioGroup
                selected={signatureAlignment}
                handleChange={tag => handleOnChangeAlignment(tag, 'signature')}
              />
            </Box>
          </Fragment>
        );
      default:
        return 'Acceptance Info Block';
    }
  };

  return (
    <Grid container direction='row' className='container' gap={2}>
      <Grid item xs={12}>
        {getSidebar(activeSidebar)}
      </Grid>
    </Grid>
  );
}
